/**
 * QA benchmark runner — a timed, self-describing performance capture.
 *
 * Goal: anyone (esp. QA, on a device, with no dev console) can sit on a table
 * with players, run a fixed-duration benchmark, and get a shareable report to
 * compare before vs. after a feature ships. It does NOT drive the game — the
 * tester navigates to the scenario (e.g. a bot table); the runner passively
 * observes for the duration and auto-describes what it saw by reading the Redux
 * store (table, stakes, seated players, hands played).
 *
 * How it runs: PerfHud already samples the HUD stats 4×/sec. While a benchmark is
 * active, PerfHud feeds each sample here via `sampleBenchmark`, drives the
 * countdown, and calls `finishBenchmark` when the timer elapses. Timing lives in
 * the HUD tick (250ms granularity) so there's no extra interval and no cross-
 * module callback. Spike attribution comes from spikeRecorder (reset at start so
 * offenders reflect only this run).
 *
 * Reports persist in localStorage so QA can accumulate before/after runs and dump
 * them together. Sharing prefers the native share sheet (navigator.share), then
 * clipboard, then console — whatever the device supports.
 */
import store from 'store';

import { getSpikeSummary, resetSpikes } from './spikeRecorder';

const STORAGE_KEY = 'hijack_perf_benchmarks';
const MAX_REPORTS = 40;
const DEFAULT_DURATION_SEC = 60;

/** Fields PerfHud feeds each sample. Its Stats object is a structural superset. */
export type BenchSample = {
  fps: number;
  worstFrameMs: number; // per-sample worst (last 250ms)
  heapUsedMb: number; // -1 where unsupported (iOS)
  heapLeakMbPerMin: number;
  heapLeakReady: boolean;
  activeTweens: number;
  wsRttMs: number; // -1 when no sample
  msgsPerSec: number;
};

export type BenchmarkReport = {
  label: string;
  at: string; // ISO
  build: string;
  device: string;
  url: string;
  durationSec: number;
  scene: {
    context: 'table' | 'lobby/other';
    gameId: string;
    title: string;
    stakes: string;
    gameType: string;
    openTablesMax: number;
    seatedPlayersMax: number;
    handsObserved: number;
    approxMessages: number;
  };
  fps: { avg: number; min: number; samples: number };
  worstFrameMs: number; // worst single sample across the run
  spikes: { total: number; worstMs: number; mode: string; topOffenders: string[] };
  memory: {
    supported: boolean;
    usedStartMb: number;
    usedEndMb: number;
    peakMb: number;
    leakMbPerMin: number | null;
  };
  tweens: { peakActive: number };
  rtt: { avgMs: number | null; peakMs: number | null };
  // Replay speed (× real time) for a replay benchmark; null for a live run.
  replaySpeed: number | null;
  // Downsampled per-sample series for the report sparklines (empty if no data).
  series: { fps: number[]; worstMs: number[]; memMb: number[] };
};

type RunState = {
  label: string;
  startedAt: number; // Date.now()
  endAt: number; // Date.now() + duration
  durationSec: number;
  fpsSamples: number[];
  // Per-sample time series for the report sparklines (downsampled at finish).
  worstSeries: number[];
  memSeries: number[];
  worstFrameMax: number;
  heapStart: number;
  heapEnd: number;
  heapPeak: number;
  lastLeak: number | null;
  tweensPeak: number;
  rttSamples: number[];
  rttPeak: number;
  msgAccum: number; // sum of msgsPerSec — ×0.25 (sample interval) ≈ total msgs
  openTablesMax: number;
  seatedMax: number;
  hands: Set<string>;
  // Replay speed for a replay benchmark (× real time); null for a live run.
  replaySpeed: number | null;
};

let run: RunState | null = null;

const readBuild = (): string =>
  process.env.REACT_APP_VERSION || process.env.REACT_APP_STAGE || process.env.NODE_ENV || 'dev';

const readDevice = (): string => {
  try {
    const ua = navigator.userAgent || 'unknown';
    return ua.length > 180 ? ua.slice(0, 180) + '…' : ua;
  } catch {
    return 'unknown';
  }
};

/** Read table context from the store. Safe if the store shape is partial. */
const readScene = () => {
  try {
    const s: any = store.getState();
    const game = s?.currentTable?.game ?? {};
    const players = Array.isArray(s?.currentTable?.players) ? s.currentTable.players : [];
    const seated = players.filter((p: any) => p && p.GUID).length;
    const gameId = String(game.gameID ?? 0);
    const atTable = gameId !== '0' && gameId !== '' && seated > 0;
    // Every table in state.tables with a real gameID is an "open" (subscribed)
    // table — the count that matters for the multi-table observe max-load case.
    const tables = s?.tables ?? {};
    const openTables = Object.values(tables).filter(
      (t: any) => t?.game?.gameID && t.game.gameID !== 0
    ).length;
    return {
      atTable,
      gameId,
      title: String(game.title ?? ''),
      stakes: game.sb !== null && game.bb !== null ? `${game.sb}/${game.bb}` : '',
      gameType: String(game.game || game.gameType || ''),
      seated,
      openTables,
      hand: String(game.hand ?? '')
    };
  } catch {
    return {
      atTable: false,
      gameId: '0',
      title: '',
      stakes: '',
      gameType: '',
      seated: 0,
      openTables: 0,
      hand: ''
    };
  }
};

const avg = (xs: number[]): number => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

// Keep report series small (for storage + the modal sparkline) by striding down
// to at most `max` points. Rounds values to whole numbers.
const downsample = (xs: number[], max = 120): number[] => {
  if (xs.length <= max) return xs.map((v) => Math.round(v));
  const stride = xs.length / max;
  const out: number[] = [];
  for (let i = 0; i < max; i++) out.push(Math.round(xs[Math.floor(i * stride)]));
  return out;
};

/** A live cash table the benchmark can observe (busiest = most seats taken). */
export type LiveTablePick = { gameID: string; gameType: string; seatsTaken: number };

/**
 * Find the busiest live cash tables from the lobby — the ones with the most
 * seated players — so the benchmark can auto-observe real, active tables.
 * Returns up to `count` distinct tables, busiest first; empty if the lobby has
 * no occupied table (not loaded, or all empty).
 *
 * Reads several lobby pools and dedupes by gameID; `seatsTaken`/`seats`/`game`
 * are the same fields the LobbyTable rows use.
 */
export const pickLiveTables = (count: number): LiveTablePick[] => {
  try {
    const lobby: any = store.getState()?.lobby ?? {};
    const pools = [lobby.gameTypeFiltered, lobby.texasCashGames, lobby.omahaCashGames];
    const byId = new Map<string, LiveTablePick>();
    for (const pool of pools) {
      if (!Array.isArray(pool)) continue;
      for (const row of pool) {
        const id = String(row?.gameID ?? '');
        const seatsTaken = Number(row?.seatsTaken) || 0;
        if (!id || seatsTaken <= 0 || byId.has(id)) continue;
        byId.set(id, { gameID: id, gameType: String(row.game || 'texas'), seatsTaken });
      }
    }
    return Array.from(byId.values())
      .sort((a, b) => b.seatsTaken - a.seatsTaken)
      .slice(0, Math.max(0, count));
  } catch {
    return [];
  }
};

/** The single busiest live table, or null. */
export const pickLiveTable = (): LiveTablePick | null => pickLiveTables(1)[0] ?? null;

/**
 * Bridge to the app's table controls. PerfHud lives OUTSIDE ViewManagerProvider,
 * so it can't call openTable/returnToLobby directly. BenchmarkTableBridge (inside
 * the provider) registers them here so the multi-table observe benchmark can
 * replay the exact "click a lobby table to observe it" action a human uses.
 */
export type TableController = {
  openTable: (
    gameID: string,
    gameType: string,
    observe: string,
    opts?: { forceSingle?: boolean }
  ) => void;
  openGrid: () => void;
  returnToLobby: () => void;
};
let tableController: TableController | null = null;
export const registerTableController = (c: TableController | null): void => {
  tableController = c;
};
export const isTableControllerReady = (): boolean => tableController !== null;

/** Open a single table as an observer via the real view transition (not a raw
 *  navigate, which doesn't slide the view). Used by hand replay. */
export const openObserveTable = (gameID: string, gameType: string): void => {
  // forceSingle bypasses the "already open + multi-table → grid" branch in
  // openTable, so replay always lands in the single-table view.
  tableController?.openTable(gameID, gameType, 'yes', { forceSingle: true });
};
/** Open the multi-table grid via the real view transition (for multi-table replay). */
export const openGridViaController = (): void => {
  tableController?.openGrid();
};
/** Return to the lobby via the real view transition. */
export const returnToLobbyViaController = (): void => {
  tableController?.returnToLobby();
};

let observeAborted = false;
const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Abort an in-progress observeTables sequence (e.g. if the HUD closes). */
export const cancelObserve = (): void => {
  observeAborted = true;
};

/**
 * Open the busiest `count` live tables as an observer, one after another —
 * exactly the human flow: observe a table, return to the lobby, observe the
 * next, until all are open. Each stays open (its Redux state.tables entry keeps
 * streaming), so the end state is `count` tables observed at once — the max-load
 * case. Resolves with how many were opened (leaves you on the last one).
 */
export const observeTables = async (
  count: number,
  opts: {
    openDelayMs?: number;
    lobbyDelayMs?: number;
    onProgress?: (opened: number, total: number, pick: LiveTablePick) => void;
  } = {}
): Promise<number> => {
  if (!tableController) return 0;
  const openDelayMs = opts.openDelayMs ?? 3000;
  const lobbyDelayMs = opts.lobbyDelayMs ?? 800;
  const picks = pickLiveTables(count);
  observeAborted = false;
  for (let i = 0; i < picks.length; i++) {
    if (observeAborted) break;
    // Route through the lobby before each additional table (matches how a human
    // opens the next one, and keeps the lobby list fresh for the pick).
    if (i > 0) {
      tableController.returnToLobby();
      await delay(lobbyDelayMs);
      if (observeAborted) break;
    }
    const pick = picks[i];
    opts.onProgress?.(i + 1, picks.length, pick);
    tableController.openTable(pick.gameID, pick.gameType, 'yes');
    await delay(openDelayMs); // let it subscribe + start streaming
  }
  return picks.length;
};

/** Is a benchmark currently running? */
export const isBenchmarkRunning = (): boolean => run !== null;

// When true, the run ignores its timer and is finished manually (used by the
// replay benchmark, which ends when the replay ends, not on a clock).
let benchmarkManualEnd = false;
export const isBenchmarkManualEnd = (): boolean => benchmarkManualEnd;

/** Ms left in the current run (<=0 means the HUD should call finishBenchmark). */
export const benchmarkRemainingMs = (): number => (run ? run.endAt - Date.now() : 0);

/** The label of the active run, for the HUD countdown display. */
export const benchmarkLabel = (): string => run?.label ?? '';

/**
 * Start a timed benchmark. Resets spike attribution so the report reflects only
 * this run. PerfHud must be open (it drives sampling + completion).
 */
export const startBenchmark = (
  label: string,
  durationSec = DEFAULT_DURATION_SEC,
  opts?: { manualEnd?: boolean; replaySpeed?: number }
): void => {
  resetSpikes();
  benchmarkManualEnd = !!opts?.manualEnd;
  const scene = readScene();
  const now = Date.now();
  run = {
    label: label || 'run',
    startedAt: now,
    endAt: now + durationSec * 1000,
    durationSec,
    fpsSamples: [],
    worstSeries: [],
    memSeries: [],
    worstFrameMax: 0,
    heapStart: -1,
    heapEnd: -1,
    heapPeak: -1,
    lastLeak: null,
    tweensPeak: 0,
    rttSamples: [],
    rttPeak: -1,
    msgAccum: 0,
    openTablesMax: scene.openTables,
    seatedMax: scene.seated,
    hands: new Set(scene.hand ? [scene.hand] : []),
    replaySpeed: opts?.replaySpeed ?? null
  };
  // eslint-disable-next-line no-console
  console.log(
    `Benchmark started: "${run.label}" for ${durationSec}s${
      scene.atTable
        ? ` — table ${scene.gameId}, ${scene.seated} seated`
        : ' — WARNING: not at a table'
    }`
  );
};

/** Feed one HUD sample into the active run. No-op if not running. */
export const sampleBenchmark = (s: BenchSample): void => {
  if (!run) return;
  if (s.fps > 0) run.fpsSamples.push(s.fps);
  run.worstSeries.push(s.worstFrameMs);
  if (s.worstFrameMs > run.worstFrameMax) run.worstFrameMax = s.worstFrameMs;
  if (s.activeTweens > run.tweensPeak) run.tweensPeak = s.activeTweens;
  if (s.heapUsedMb >= 0) {
    run.memSeries.push(s.heapUsedMb);
    if (run.heapStart < 0) run.heapStart = s.heapUsedMb;
    run.heapEnd = s.heapUsedMb;
    if (s.heapUsedMb > run.heapPeak) run.heapPeak = s.heapUsedMb;
    if (s.heapLeakReady) run.lastLeak = s.heapLeakMbPerMin;
  }
  if (s.wsRttMs >= 0) {
    run.rttSamples.push(s.wsRttMs);
    if (s.wsRttMs > run.rttPeak) run.rttPeak = s.wsRttMs;
  }
  run.msgAccum += s.msgsPerSec;

  // Re-read scene each sample so open-tables, seated-count and hands-played
  // reflect the whole run.
  const scene = readScene();
  if (scene.openTables > run.openTablesMax) run.openTablesMax = scene.openTables;
  if (scene.seated > run.seatedMax) run.seatedMax = scene.seated;
  if (scene.hand) run.hands.add(scene.hand);
};

/**
 * Finalize the active run into a report, persist it, and return it. No-op-safe:
 * returns null if nothing is running.
 */
export const finishBenchmark = (): BenchmarkReport | null => {
  if (!run) return null;
  const r = run;
  run = null;
  benchmarkManualEnd = false;

  const spikes = getSpikeSummary();
  const scene = readScene();
  const memSupported = r.heapPeak >= 0;
  // msgAccum sums per-second rates sampled 4×/sec, so ÷4 ≈ total messages.
  const approxMessages = Math.round(r.msgAccum / 4);

  const report: BenchmarkReport = {
    label: r.label,
    at: new Date().toISOString(),
    build: readBuild(),
    device: readDevice(),
    url: (() => {
      try {
        return window.location.href;
      } catch {
        return '';
      }
    })(),
    // Actual wall-clock elapsed — for a replay (manual end) the passed duration
    // is 0, so measure it from the start instead of trusting the timer value.
    durationSec: Math.max(1, Math.round((Date.now() - r.startedAt) / 1000)),
    scene: {
      context: scene.atTable ? 'table' : 'lobby/other',
      gameId: scene.gameId,
      title: scene.title,
      stakes: scene.stakes,
      gameType: scene.gameType,
      openTablesMax: r.openTablesMax,
      seatedPlayersMax: r.seatedMax,
      handsObserved: r.hands.size,
      approxMessages
    },
    fps: {
      avg: Math.round(avg(r.fpsSamples)),
      min: r.fpsSamples.length ? Math.min(...r.fpsSamples) : 0,
      samples: r.fpsSamples.length
    },
    worstFrameMs: Math.round(r.worstFrameMax),
    spikes: {
      total: spikes.total,
      worstMs: Math.round(spikes.worstMs),
      mode: spikes.mode,
      topOffenders: spikes.offenders
        .slice(0, 5)
        .map((o) => `${o.key} (${Math.round(o.totalMs)}ms, ${o.hits} hits)`)
    },
    memory: {
      supported: memSupported,
      usedStartMb: Math.round(r.heapStart),
      usedEndMb: Math.round(r.heapEnd),
      peakMb: Math.round(r.heapPeak),
      leakMbPerMin: r.lastLeak !== null ? Math.round(r.lastLeak * 10) / 10 : null
    },
    tweens: { peakActive: r.tweensPeak },
    rtt: {
      avgMs: r.rttSamples.length ? Math.round(avg(r.rttSamples)) : null,
      peakMs: r.rttPeak >= 0 ? Math.round(r.rttPeak) : null
    },
    replaySpeed: r.replaySpeed,
    series: {
      fps: downsample(r.fpsSamples),
      worstMs: downsample(r.worstSeries),
      memMb: downsample(r.memSeries)
    }
  };

  const all = loadReports();
  all.push(report);
  saveReports(all);
  // eslint-disable-next-line no-console
  console.log(formatBenchmarkReport(report));
  return report;
};

/** Abort the active run without producing a report. */
export const cancelBenchmark = (): void => {
  run = null;
  benchmarkManualEnd = false;
};

const loadReports = (): BenchmarkReport[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveReports = (reports: BenchmarkReport[]): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(reports.slice(-MAX_REPORTS)));
  } catch {
    // localStorage unavailable — the run's report is still returned/logged/shared.
  }
};

/** All saved reports (for `copy(getBenchmarks())` or comparison). */
export const getBenchmarks = (): BenchmarkReport[] => loadReports();

/** Wipe saved reports. */
export const clearBenchmarks = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/** Human-readable, paste-into-a-ticket rendering of one report. */
export const formatBenchmarkReport = (r: BenchmarkReport): string => {
  const lines = [
    `=== PERF BENCHMARK: ${r.label} ===`,
    `when:     ${r.at}`,
    `build:    ${r.build}`,
    `device:   ${r.device}`,
    `duration: ${r.durationSec}s`,
    `source:   ${r.replaySpeed !== null ? `replay @ ${r.replaySpeed}× speed` : 'live'}`,
    ``,
    `SCENE`,
    `  context:  ${r.scene.context}`,
    `  open tables: ${r.scene.openTablesMax} (observed at once, max)`,
    `  focus:    ${r.scene.gameId}${r.scene.title ? ` (${r.scene.title})` : ''}`,
    `  stakes:   ${r.scene.stakes || '—'}   type: ${r.scene.gameType || '—'}`,
    `  seated:   ${r.scene.seatedPlayersMax} players (focused table, max)`,
    `  hands:    ${r.scene.handsObserved} observed`,
    `  messages: ~${r.scene.approxMessages} inbound`,
    ``,
    `FRAMES`,
    `  fps:         avg ${r.fps.avg}, min ${r.fps.min} (${r.fps.samples} samples)`,
    `  worst frame: ${r.worstFrameMs}ms`,
    `  spikes:      ${r.spikes.total} (worst ${r.spikes.worstMs}ms, ${r.spikes.mode})`,
    ...(r.spikes.topOffenders.length
      ? [`  top offenders:`, ...r.spikes.topOffenders.map((o) => `    - ${o}`)]
      : [`  top offenders: none (or unsupported — iOS)`]),
    ``,
    `MEMORY`,
    ...(r.memory.supported
      ? [
          `  heap:  ${r.memory.usedStartMb} → ${r.memory.usedEndMb}MB (peak ${r.memory.peakMb})`,
          `  leak:  ${
            r.memory.leakMbPerMin !== null ? `${r.memory.leakMbPerMin} MB/min` : 'n/a (too short)'
          }`
        ]
      : [`  n/a (unsupported on this platform — iOS)`]),
    ``,
    `ANIMATION`,
    `  tweens peak: ${r.tweens.peakActive}`,
    ``,
    `NETWORK`,
    `  ws rtt: ${
      r.rtt.avgMs !== null ? `avg ${r.rtt.avgMs}ms, peak ${r.rtt.peakMs}ms` : 'no samples'
    }`,
    `====================================`
  ];
  return lines.join('\n');
};

/**
 * Share a report: native share sheet where available (best for QA on a phone),
 * else copy to clipboard, else log. Should be called from a user gesture (e.g. a
 * HUD button tap) so navigator.share isn't blocked.
 */
export const shareBenchmark = async (report?: BenchmarkReport): Promise<void> => {
  const r = report ?? loadReports().slice(-1)[0];
  if (!r) {
    // eslint-disable-next-line no-console
    console.log('No benchmark to share yet.');
    return;
  }
  const text = formatBenchmarkReport(r);
  try {
    const nav = navigator as any;
    if (typeof nav.share === 'function') {
      await nav.share({ title: `Perf benchmark: ${r.label}`, text });
      return;
    }
  } catch {
    // fall through to clipboard
  }
  try {
    await navigator.clipboard.writeText(text);
    // eslint-disable-next-line no-console
    console.log('Benchmark report copied to clipboard.');
    return;
  } catch {
    // eslint-disable-next-line no-console
    console.log(text);
  }
};

/** console.table comparing every saved report — the before/after grid. */
export const dumpBenchmarks = (): BenchmarkReport[] => {
  const reports = loadReports();
  if (reports.length === 0) {
    // eslint-disable-next-line no-console
    console.log(
      'No benchmarks yet. Run a benchmark from the PerfHud (▶) or runBenchmark("label").'
    );
    return reports;
  }
  /* eslint-disable no-console */
  console.table(
    reports.map((r) => ({
      label: r.label,
      when: r.at.slice(5, 16).replace('T', ' '),
      speed: r.replaySpeed !== null ? `${r.replaySpeed}×` : 'live',
      tables: r.scene.openTablesMax,
      seated: r.scene.seatedPlayersMax,
      hands: r.scene.handsObserved,
      'fps avg': r.fps.avg,
      'fps min': r.fps.min,
      'worst ms': r.worstFrameMs,
      spikes: r.spikes.total,
      'spike worst': r.spikes.worstMs,
      'mem peak': r.memory.supported ? r.memory.peakMb : '—',
      'leak/min': r.memory.leakMbPerMin ?? '—',
      'tweens peak': r.tweens.peakActive,
      'rtt peak': r.rtt.peakMs ?? '—'
    }))
  );
  console.log(`${reports.length} benchmarks. copy(getBenchmarks()) for full JSON.`);
  /* eslint-enable no-console */
  return reports;
};
